import { z } from "zod";
import { MEDIA_PREVIEW_EDGE, MediaName } from "./media.ts";
import { PLAN_MAX_SLIDES, PlanTitle } from "./plans.ts";
import { Lesson } from "./room.ts";

/**
 * Импорт документа в заготовку урока: преподаватель кладёт PDF в «Материалы»,
 * потом отдельным действием выбирает из него страницы и получает заготовку, где
 * каждая выбранная страница стоит подложкой слайда.
 *
 * Три решения, из которых следует всё остальное (подробности в
 * docs/pptx-import-design.md):
 *
 * 1. **Растеризация на сервере, клиентской ветки нет.** Два растеризатора это
 *    два разных вида одного документа: страница, собранная сегодня и через
 *    неделю, выглядела бы по-разному. Побочный выигрыш виден человеку: вкладку
 *    можно закрыть, конвертация продолжится
 * 2. **Конвертация это отдельная операция, а не побочный эффект загрузки.**
 *    Загрузка и нарезка ломаются и длятся по-разному, а PDF не всегда режется
 *    целиком: справочник на триста страниц это две страницы для урока
 * 3. **Собирается ЗАГОТОВКА, а не урок.** Урок принадлежит паре и дате,
 *    заготовка ничья и переиспользуется. Слова «создать урок из PDF» не должно
 *    быть нигде, включая подсказки
 */

// ------------------------------------------------------------------- растр

/**
 * В какой прямоугольник вписывается страница. 1,25 логического размера слайда
 * (1280x720): на ноутбуке 15 дюймов с двойной плотностью полезная ширина сцены
 * около 900 CSS-пикселей, то есть 1800 физических, и 1600 туда почти попадает.
 * 1920x1080 дало бы на 40% больше байт ради разницы, заметной только на ретине,
 * а байты едут по каналу 128 кбит/с (раздел 3.1 CLAUDE.md).
 */
export const IMPORT_RASTER_WIDTH = 1600;
export const IMPORT_RASTER_HEIGHT = 900;

/**
 * Миниатюра для экрана нарезки: та же длинная сторона, что у плиток в
 * «Материалах». Второго числа под то же понятие заводить нельзя.
 */
export const IMPORT_THUMB_EDGE = MEDIA_PREVIEW_EDGE;

/** Качество WebP. `smartSubsample` включён, см. `render_profile` ниже. */
export const IMPORT_WEBP_QUALITY = 82;

/**
 * Масштаб отрисовки одной страницы. Правило одно на все случаи: вписываем в
 * прямоугольник, поля по бокам. Не растягиваем (растянутый слайд выглядит как
 * брак), не обрезаем (у A4-раздатки по краям текст, а не поля), не поворачиваем.
 *
 * ⚠️ Страница нулевого размера сюда приходить не должна: `min(1600/0, 900/0)`
 * это `Infinity`, а дальше попытка выделить битмап невообразимого размера.
 * Возвращаем 0, и вызывающий обязан такую страницу пропустить.
 */
export function rasterScale(widthPt: number, heightPt: number): number {
  if (!(widthPt > 0) || !(heightPt > 0)) return 0;
  return Math.min(IMPORT_RASTER_WIDTH / widthPt, IMPORT_RASTER_HEIGHT / heightPt);
}

/**
 * Длинная сторона крупной страницы книги (`docs/pdf-book-view-design.md`, Р5),
 * пиксели. Книжная страница выходит 1130x1600.
 *
 * Довод от экрана: iPhone шириной 390 CSS-пикселей при плотности 3 это 1170
 * физических, страница во всю ширину почти пиксель в пиксель; ноутбук с
 * плотностью 2 и окном высотой около 900 это 1800. 1200 дало бы вдвое меньше
 * байт, но на телефоне страница была бы растянута в полтора раза ещё до
 * увеличения.
 *
 * ⚠️ Своя константа, а не `READ_EDGE` отрисовки чтения: то число подбирали под
 * модель, и совпадение здесь не ссылка.
 */
export const PAGE_VIEW_EDGE = 1600;

/** Портретная страница встанет с полями по бокам. Об этом предупреждаем заранее. */
export function isPortraitPage(widthPt: number, heightPt: number): boolean {
  return widthPt > 0 && heightPt > 0 && heightPt > widthPt;
}

// ---------------------------------------------------------------- потолки

/**
 * Жёсткий потолок на длину массива страниц в теле запроса.
 *
 * ⚠️ Это НЕ продуктовое ограничение. Сколько страниц берём за одно задание,
 * решает настройка `IMPORT_MAX_PAGES` у API: число это не про базу, а про то,
 * сколько человек готов ждать, и подбирается оно на живой машине, без
 * пересборки фронта. Здесь стоит только рубеж против нелепого тела запроса:
 * схема общая фронту и бэку, а env она читать не может.
 *
 * Настройка обязана быть не выше этого числа, и API проверяет это при старте.
 * Разъехавшись, они дали бы выбор, который схема принимает, а ручка отклоняет.
 */
export const IMPORT_PAGES_HARD_MAX = 2000;

/**
 * Незавершённых заданий на человека. Не про ресурсы (их ест единственный
 * исполнитель, а не число строк), а против случайного самообстрела: выделил
 * десять PDF, нажал «Собрать заготовку» на всех и забил единственный слот себе
 * же на полчаса.
 */
export const IMPORT_MAX_LIVE_JOBS = 3;

/** Попыток на задание. Дальше это не «не повезло», а «файл такой». */
export const IMPORT_MAX_ATTEMPTS = 3;

/**
 * Потолок страниц за одну вставку в доску урока
 * (`docs/room-material-insert-design.md`, Р3). Тридцать слайдов это около 7 КБ
 * одного обновления документа, ниже обрыва 16 КБ (раздел 3 CLAUDE.md), а урок
 * не место для учебника целиком. Действует потолок вместе с настройкой
 * `IMPORT_MAX_PAGES`, поэтому число берут только через `lessonImportMaxPages`:
 * сервер при выборе страниц, окно и тексты одной формулой.
 */
export const LESSON_IMPORT_MAX_PAGES = 30;

export function lessonImportMaxPages(maxPages: number): number {
  return Math.min(LESSON_IMPORT_MAX_PAGES, maxPages);
}

/**
 * Во сколько оцениваем страницу при проверке квоты ДО работы. Текстовая
 * страница это 60-120 КБ, страница с фотографией во весь экран 200-350 КБ.
 * Берём с запасом: отказ на середине дороже лишней осторожности в начале.
 */
export const IMPORT_ESTIMATE_BYTES = 200 * 1024;

/** Корень для нарезанных страниц. Внутри папка на документ. */
export const IMPORT_FOLDER_ROOT = "Импорт";

/**
 * До скольких символов усекаем имя исходника перед подстановкой в имя папки.
 * ⚠️ `MediaFolderName` ограничен 120 символами, а имя файла двумястами, то есть
 * без усечения импорт файла с длинным именем падал бы на валидации уже ПОСЛЕ
 * успешной отрисовки. Сто, а не сто двадцать: запас нужен под разрешение
 * конфликта имён.
 */
export const IMPORT_NAME_LIMIT = 100;

// ------------------------------------------------------------- состояния

/**
 * Состояние задания.
 *
 * `probing` это «считаем страницы и рисуем миниатюры», `awaiting_choice` это
 * открытый экран нарезки, дальше очередь и работа. Терминальных три, и все три
 * оставляют одинаковый след: нарисованные страницы лежат в «Материалах», а
 * заготовка либо создана целиком, либо не создана вовсе.
 */
export const ImportJobState = z.enum([
  "probing",
  "awaiting_choice",
  "queued",
  "running",
  "done",
  "failed",
  "cancelled",
]);
export type ImportJobState = z.infer<typeof ImportJobState>;

export const IMPORT_TERMINAL_STATES: readonly ImportJobState[] = ["done", "failed", "cancelled"];

export function isTerminalImportState(state: ImportJobState): boolean {
  return IMPORT_TERMINAL_STATES.includes(state);
}

/**
 * Документ разобран целиком: строк страниц столько, сколько насчитал
 * завершённый разбор, и у каждой есть миниатюра либо страница битая (миниатюры
 * у неё не будет никогда). Одна проверка на быстрый путь исполнителя и на вход
 * без ожидания исполнителя (`docs/room-material-insert-design.md`, 3.4).
 *
 * ⚠️ «Целиком» проверяется по числу, а не по виду строк. Разбор могли прервать
 * на середине, и тогда строки выглядят целыми: у каждой есть миниатюра, просто
 * их четырнадцать вместо сорока. Ошибка молчаливая: человек увидел бы
 * усечённый документ и решил, что так и есть. Разъехавшись между исполнителем и
 * ручкой входа, проверка дала бы документ, который один путь считает
 * разобранным, а другой каждый раз разбирает заново.
 */
export function isProbeComplete(
  pages: readonly { hasThumb: boolean; widthPt: number; heightPt: number }[],
  expected: number | null,
): boolean {
  return (
    pages.length > 0 &&
    pages.length === expected &&
    pages.every((p) => p.hasThumb || !(p.widthPt > 0 && p.heightPt > 0))
  );
}

/**
 * Страница исходного документа так, как её видит экран нарезки.
 *
 * `broken` это страница нулевого размера: её плитка подписана и не выбирается,
 * а остальные работают. Одна битая страница не должна отменять весь остальной
 * учебник.
 */
export const ImportPage = z.object({
  no: z.number().int().positive(),
  widthPt: z.number(),
  heightPt: z.number(),
  /** Миниатюра готова: плитку можно рисовать картинкой, а не рамкой с номером. */
  hasThumb: z.boolean(),
  broken: z.boolean(),
  /** Страница уже нарисована в прошлый заход: второй раз за неё не платят. */
  cached: z.boolean(),
});
export type ImportPage = z.infer<typeof ImportPage>;

/**
 * Нарисованная страница готового задания урока: что ставить подложкой слайда
 * (`docs/room-material-insert-design.md`, 3.4). Размеры из `media_file`, у
 * которого они бывают пустыми.
 */
export const ImportRender = z.object({
  no: z.number().int().positive(),
  fileId: z.string().uuid(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
});
export type ImportRender = z.infer<typeof ImportRender>;

/**
 * Состояние задания целиком. Клиент опрашивает эту ручку раз в полторы-две
 * секунды: долгоживущий WebSocket на домене API запрещён разделом 4 CLAUDE.md,
 * а тащить прогресс в `edu-ws` значило бы уронить импорт вместе с доской.
 */
export const ImportJobStatus = z.object({
  jobId: z.string().uuid(),
  sourceFileId: z.string().uuid(),
  sourceName: z.string(),
  state: ImportJobState,
  title: z.string(),
  totalPages: z.number().int().nonnegative().nullable(),
  /**
   * Потолок страниц за одно задание, из настроек API. Приезжает в ответе по той
   * же причине, что `limitBytes` в квоте: число живёт в env, а показать его и
   * проверить выбор до отправки обязан экран.
   */
  maxPages: z.number().int().positive(),
  /** Готово миниатюр на `probing`, готово страниц на `running`. */
  donePages: z.number().int().nonnegative(),
  pages: z.array(ImportPage),
  /**
   * Подпись на миниатюры ЭТОГО документа, одна на все страницы. Номер страницы
   * в подпись не входит намеренно: она открывает ровно то, что и так целиком
   * показано на экране, а подпись на каждую плитку была бы сотнями токенов в
   * одном ответе ради разграничения, которого нет.
   */
  thumbToken: z.string().nullable(),
  /**
   * Момент истечения `thumbToken`, мс (`docs/pdf-book-view-design.md`, Р14):
   * экран держит подпись до минуты до него, а не берёт новую на каждый опрос.
   * `default(null)`: при старом сервере экран держит подпись пять минут, как
   * раньше.
   */
  thumbTokenExpiresAt: z.number().int().nullable().default(null),
  /** Что человек выбрал в прошлый раз. Экран восстанавливает галочки по нему. */
  selectedPages: z.array(z.number().int().positive()).nullable(),
  splitEvery: z.number().int().positive().nullable(),
  createdPlanIds: z.array(z.string().uuid()),
  /** Сколько заготовок должно получиться. null, пока страницы не выбраны. */
  plannedPlans: z.number().int().positive().nullable(),
  errorCode: z.string().nullable(),
  /**
   * Что мы заметили в файле, не отказывая: маркеры активного содержимого.
   * Отдельно от `errorCode`, потому что это не отказ, а то, о чём человеку
   * стоит знать про присланный ему файл.
   */
  warnings: z.array(z.string()).default([]),
  attempts: z.number().int().nonnegative(),
  /** Сколько заданий впереди в общей очереди. Исполнитель один на всю машину. */
  queueAhead: z.number().int().nonnegative(),
  /** Куда легли страницы. null, пока не нарисовано ни одной. */
  pagesFolderId: z.string().uuid().nullable(),
  /**
   * Дописывание в существующую заготовку (`docs/pdf-append-design.md`, 3.9).
   * Режим экрана берётся отсюда, а не из адреса: побеждает последнее
   * намерение, и цель могли сменить из другого окна. `null` у задания без цели.
   *
   * Все поля `default(null)`: окно со старым кодом сайта разбирает ответ
   * сервера, который их уже отдаёт, а новое окно разбирает ответ старого.
   */
  targetPlanId: z.string().uuid().nullable().default(null),
  targetTitle: z.string().nullable().default(null),
  /** Слайдов в цели сейчас. Бывает больше 60 после перебазирования (3.4). */
  targetSlides: z.number().int().nonnegative().nullable().default(null),
  /**
   * Сколько мест в цели держат ДРУГИЕ живые дописывания (`queued`, `running`):
   * экран выбора считает по нему, сколько ещё поместится (`appendRoom`).
   */
  targetReserved: z.number().int().nonnegative().nullable().default(null),
  /**
   * Занятия по цели, которые материал уже взяли и сами новых страниц не
   * получат (3.6). Считаются только в `done`; до того `null`.
   */
  targetLessonsToUpdate: z.array(Lesson).nullable().default(null),
  /** Сколько таких занятий не попало в список сверх потолка чтения. */
  targetLessonsMore: z.number().int().nonnegative().nullable().default(null),
  /**
   * Страницы для доски урока (`docs/room-material-insert-design.md`, 3.4):
   * урок, для которого они готовятся, и слайд, за которым встанут (или
   * пометка «выбор ждёт» до выбора страниц, 3.7). `null` у задания заготовки.
   * Тоже `default(null)`, по той же причине, что поля цели выше.
   */
  targetLessonId: z.string().uuid().nullable().default(null),
  targetAnchor: z.string().nullable().default(null),
  /**
   * Нарисованные выбранные страницы по возрастанию номера, только у `done`
   * задания урока: это и есть то, что встанет слайдами. Страницы, которой
   * здесь нет среди выбранных, не открылась или уже в корзине.
   */
  renders: z.array(ImportRender).nullable().default(null),
});
export type ImportJobStatus = z.infer<typeof ImportJobStatus>;

// -------------------------------------------------------------- запросы

/**
 * Вход в нарезку. `targetPlanId` это заготовка, в конец которой встанут
 * страницы (`docs/pdf-append-design.md`, 3.9). Нет поля это «без цели», то есть
 * новая заготовка: так работают окна со старым кодом сайта, которые поля не
 * шлют. Живое задание по тому же PDF получает цель из запроса, пока страницы
 * не выбраны: побеждает последнее намерение, в обе стороны.
 */
export const ImportCreateRequest = z
  .object({
    fileId: z.string().uuid(),
    targetPlanId: z.string().uuid().nullable().default(null),
    /**
     * Урок, на доску которого встанут страницы
     * (`docs/room-material-insert-design.md`, 3.4). Не вместе с заготовкой:
     * цель у задания одна, и это держит ещё и `CHECK` в базе.
     */
    targetLessonId: z.string().uuid().nullable().default(null),
  })
  .refine((b) => b.targetLessonId === null || b.targetPlanId === null, {
    message: "targetLessonId не вместе с targetPlanId",
    path: ["targetLessonId"],
  });
export type ImportCreateRequest = z.infer<typeof ImportCreateRequest>;

/** Слайд доски урока, за которым встанут страницы. Id слайдов в документе это строки. */
const AnchorSlideId = z.string().min(1).max(200);

/**
 * Выбор страниц. Порядок в массиве значения не имеет: страницы встают по
 * возрастанию номера независимо от того, в каком порядке ставили галочки.
 * Иначе порядок слайдов зависел бы от настроения кликавшего.
 *
 * У задания урока (`targetLessonId`) запрос бывает двух видов
 * (`docs/room-material-insert-design.md`, раздел 4): со страницами это выбор, а
 * без страниц только якорь, то есть пометка «выбор ждёт» (3.7), которую окно
 * ставит в момент нажатия «Добавить», пока документ ещё разбирается. Без
 * `targetLessonId` всё как прежде: страницы и название обязательны.
 */
export const ImportChoiceRequest = z
  .object({
    pages: z.array(z.number().int().positive()).min(1).max(IMPORT_PAGES_HARD_MAX).optional(),
    /** Название заготовки. Задание урока заготовки не собирает и берёт прежнее. */
    title: PlanTitle.optional(),
    /**
     * По сколько страниц в заготовку. null это «все выбранные в одну». Сверху
     * ограничено `PLAN_MAX_SLIDES`: второй потолок дал бы заготовку, которую
     * импорт создал, а редактор не открывает.
     */
    splitEvery: z.number().int().positive().max(PLAN_MAX_SLIDES).nullable().default(null),
    /**
     * Цель, которую ОЖИДАЕТ экран выбора (3.9). Сервер сверяет её с целью
     * задания в той же записи, что ставит задание в очередь, и при расхождении
     * отказывает `job_target_changed`: иначе страницы, выбранные «в новую
     * заготовку», встали бы в чужую цель из другого окна, и наоборот. Нет поля
     * это «без цели», как шлют окна со старым кодом сайта.
     */
    targetPlanId: z.string().uuid().nullable().default(null),
    /** Ожидаемый урок; сверяется с уроками комнаты, а не с одним этим уроком. */
    targetLessonId: z.string().uuid().nullable().default(null),
    anchorSlideId: AnchorSlideId.nullable().default(null),
  })
  .superRefine((b, ctx) => {
    if (b.targetLessonId !== null && (b.targetPlanId !== null || b.splitEvery !== null)) {
      ctx.addIssue({ code: "custom", path: ["targetLessonId"], message: "не вместе с targetPlanId и splitEvery" });
    }
    if (b.anchorSlideId !== null && b.targetLessonId === null) {
      ctx.addIssue({ code: "custom", path: ["anchorSlideId"], message: "якорь бывает только у урока" });
    }
    if (b.pages === undefined && b.anchorSlideId === null) {
      ctx.addIssue({ code: "custom", path: ["pages"], message: "без страниц нужен якорь" });
    }
    if (b.pages !== undefined && b.targetLessonId === null && b.title === undefined) {
      ctx.addIssue({ code: "custom", path: ["title"], message: "у заготовки нужно название" });
    }
  });
export type ImportChoiceRequest = z.infer<typeof ImportChoiceRequest>;

/**
 * Отмена (`docs/room-material-insert-design.md`, 3.4). Без тела и без
 * `targetLessonId` работает как прежде, этим пользуется экран заготовки. С
 * уроком три режима:
 *
 * - без `beforeChoice` («Отменить» и крестик): живое отменяется, а `done` и
 *   `failed` убираются из строки урока;
 * - `beforeChoice` (закрытие строки «ещё не выбраны»): только задание до
 *   выбора страниц, с пометкой «выбор ждёт» или без неё;
 * - `beforeChoice` и `unmarked` («Назад» и закрытие окна на шаге страниц): то
 *   же, но только задание БЕЗ пометки. Пометку ставит вкладка, которая уже
 *   отправила выбор, и окно, брошенное в другой вкладке той же комнаты, её
 *   выбор отменить не должно.
 */
export const ImportCancelRequest = z
  .object({
    targetLessonId: z.string().uuid().nullable().default(null),
    beforeChoice: z.boolean().default(false),
    unmarked: z.boolean().default(false),
  })
  .refine((b) => !b.beforeChoice || b.targetLessonId !== null, {
    message: "beforeChoice только у урока",
    path: ["beforeChoice"],
  })
  .refine((b) => !b.unmarked || b.beforeChoice, {
    message: "unmarked только вместе с beforeChoice",
    path: ["unmarked"],
  });
export type ImportCancelRequest = z.infer<typeof ImportCancelRequest>;

/**
 * Сколько выбранных страниц поместится в цель (`docs/pdf-append-design.md`,
 * 3.3). Одна формула на экран выбора и транзакцию выбора на сервере.
 *
 * `reserved` это места, которые держат ДРУГИЕ живые дописывания в ту же цель.
 * Против 60 строго: дописывание само больше 60 не делает, а слайдов больше 60
 * (до 120) бывает только после перебазирования, и тогда `fits` это 0.
 * `excess` это сколько галочек снять, чтобы выбор прошёл.
 */
export interface AppendRoom {
  fits: number;
  excess: number;
}

export function appendRoom(args: { slideCount: number; reserved: number; selected: number }): AppendRoom {
  const fits = Math.max(0, PLAN_MAX_SLIDES - args.slideCount - args.reserved);
  return { fits, excess: Math.max(0, args.selected - fits) };
}

/** Сколько заготовок выйдет из выбранного. Одна формула на клиент и сервер. */
export function plannedPlanCount(pageCount: number, splitEvery: number | null): number {
  return splitEvery === null ? 1 : Math.ceil(pageCount / splitEvery);
}

/**
 * Имя файла страницы: `<имя> 007.webp`, ведущие нули по разрядности общего
 * числа страниц. Так сортировка по имени в проводнике совпадает с порядком
 * страниц, а иначе десятая страница встаёт между первой и второй.
 */
export function importedPageName(base: string, pageNo: number, totalPages: number): string {
  const digits = Math.max(2, String(totalPages).length);
  return `${base} ${String(pageNo).padStart(digits, "0")}.webp`;
}

/** Имя без расширения, усечённое до потолка папки. Пустое имя не отдаём. */
export function importBaseName(fileName: string): string {
  const cut = fileName.replace(/\.[^.]{1,10}$/, "").trim();
  const short = (cut === "" ? fileName : cut).slice(0, IMPORT_NAME_LIMIT).trim();
  return short === "" ? "Документ" : short;
}

/**
 * Имя папки на документ. Разделители пути и управляющие символы заменяем
 * пробелом: `MediaName` их запрещает, а имя файла с ними прийти может, и падать
 * на валидации уже ПОСЛЕ успешной отрисовки страниц нельзя.
 */
export function importFolderName(fileName: string): string {
  const cleaned = importBaseName(fileName)
    .replace(/[\u0000-\u001f\u007f/\\]/g, " ")
    .trim();
  return MediaName.parse(cleaned === "" ? "Документ" : cleaned);
}

// --------------------------------------------------------------- отказы

/**
 * Отказы словами. Код это то, что уезжает в базу и в лог, текст это то, что
 * человек читает. Держим вместе, потому что разъехавшись, они дают экран с
 * `convert_crashed` вместо объяснения.
 */
const tooManyPagesText = (max: number | null): string =>
  max === null
    ? "В документе слишком много страниц. Разделите его и загрузите частями"
    : `В документе больше ${max} страниц. Разделите его и загрузите частями`;

/** Один текст на оба словаря (итог задания и отказ ручки): правка доезжает до обоих. */
const FORMAT_NOT_SUPPORTED_TEXT = "Пока собираем заготовки только из PDF";

export const IMPORT_ERROR_TEXT: Readonly<Record<string, string>> = {
  pdf_encrypted:
    "PDF защищён паролем. Снимите пароль и загрузите файл заново: сам пароль мы не спрашиваем, хранить чужие секреты нам негде",
  doc_empty: "В этом документе нет страниц",
  doc_broken: "Файл не открылся: похоже, он повреждён или это не PDF",
  doc_too_many_pages: tooManyPagesText(null),
  raster_oom:
    "Страница оказалась слишком тяжёлой и не поместилась в память. Мы прекратили её рисовать, чтобы не задеть идущий урок",
  raster_timeout: "Отрисовка страницы не уложилась в отведённое время",
  convert_crashed: "Не удалось нарисовать страницы этого документа",
  disk_full: "На сервере кончилось место. Освободите его и повторите",
  quota_exceeded: "Не хватает места в хранилище. Освободите место или выберите меньше страниц",
  plan_full: `В заготовке не больше ${PLAN_MAX_SLIDES} слайдов`,
  source_gone: "Исходный файл удалён, нарезать больше нечего",
  job_gone: "Задание больше не существует",
  format_not_supported: FORMAT_NOT_SUPPORTED_TEXT,
};

/**
 * `maxPages` нужен ровно одному коду отказа и берётся из состояния задания.
 * Без него текст остаётся верным, просто без числа: админке, которая смотрит на
 * чужие задания списком, конкретный потолок и не нужен.
 */
export function importErrorText(code: string | null, maxPages?: number): string | null {
  if (code === null) return null;
  if (code === "doc_too_many_pages") return tooManyPagesText(maxPages ?? null);
  return IMPORT_ERROR_TEXT[code] ?? "Не удалось собрать заготовку";
}

/**
 * Отказы ручек импорта словами (`docs/pdf-append-design.md`, 3.8 и раздел 6):
 * вход в нарезку и выбор страниц. Отдельно от `IMPORT_ERROR_TEXT`: тот про
 * итог задания (`error_code` в базе), а этот про ответ ручки, который человек
 * получает сразу, нажав кнопку.
 *
 * Числа берутся из `details` ответа (`max`, `fits`, `reserved`), а не пишутся
 * здесь: зашитое число соврёт при первой правке потолка. `null` означает «код
 * не наш», и экран тогда идёт в общий разбор отказов.
 *
 * ⚠️ `plan_full` без `reserved` это отказ ВЫБОРА В НОВУЮ ЗАГОТОВКУ, и там
 * выход это разбиение; с `reserved` это дописывание в существующую, где
 * разбиения нет, а выход это снять галочки или освободить слайды.
 */
export const IMPORT_REFUSAL_TEXT: Readonly<Record<string, string>> = {
  job_not_ready:
    "Выбрать страницы пока нельзя: документ ещё открывается или страницы из него уже выбрали в другом окне. Перезагрузите вкладку",
  job_target_changed:
    "Пока вы выбирали, этот PDF открыли в другом окне. Перезагрузите вкладку: будет видно, куда теперь встанут страницы",
  target_plan_gone:
    "Заготовка в корзине, поэтому страницы в неё не добавлены. Чтобы добавить их, верните заготовку из корзины и выберите страницы снова",
  plan_not_found:
    "Заготовки нет: её убрали в корзину или удалили. Если она в корзине, верните её оттуда, чтобы добавить страницы",
  page_not_available: "Одна из выбранных страниц не открывается. Снимите с неё галочку и попробуйте снова",
  format_not_supported: FORMAT_NOT_SUPPORTED_TEXT,
  file_too_large: "Файл слишком большой, такой PDF мы не разбираем. Разделите его на части и загрузите их по отдельности",
  /**
   * Запасной текст отказа `doc_unreadable` (`docs/pdf-book-view-design.md`,
   * Р7): причина в `details.reason` не строка или незнакома. Знакомые причины
   * разворачивает экран (`importRefusal` в `features/import/import-text.ts`).
   */
  doc_unreadable: "Заготовку из этого PDF не собрать: его страницы не открываются",
};

function ruPlural(n: number, one: string, few: string, many: string): string {
  const tens = n % 100;
  const ones = n % 10;
  if (tens >= 11 && tens <= 14) return many;
  if (ones === 1) return one;
  if (ones >= 2 && ones <= 4) return few;
  return many;
}

const pagesWord = (n: number): string => ruPlural(n, "страница", "страницы", "страниц");

function countOf(details: Readonly<Record<string, unknown>>, key: string): number | null {
  const v = details[key];
  return typeof v === "number" && Number.isInteger(v) && v >= 0 ? v : null;
}

function planFullText(details: Readonly<Record<string, unknown>>): string {
  const fits = countOf(details, "fits");
  const reserved = countOf(details, "reserved");
  if (reserved === null) {
    return `В одной заготовке не больше ${String(fits ?? PLAN_MAX_SLIDES)} слайдов. Включите разбиение или выберите меньше страниц`;
  }
  if (fits === null || fits === 0) {
    return reserved > 0
      ? "Места не осталось: его займут страницы из PDF, которые сейчас добавляются в эту заготовку. Когда они добавятся, уберите слайды в заготовке и выберите страницы снова"
      : `Места не осталось: в заготовке может быть не больше ${String(PLAN_MAX_SLIDES)} слайдов. Уберите слайды в заготовке и выберите страницы снова`;
  }
  const head = `Поместится только ${String(fits)} ${pagesWord(fits)}`;
  const why = reserved > 0 ? `: ещё ${String(reserved)} ${ruPlural(reserved, "место", "места", "мест")} займут страницы из PDF, которые сейчас добавляются` : "";
  return `${head}${why}. Снимите лишние галочки или уберите слайды в заготовке`;
}

export function importRefusalText(code: string, details: Readonly<Record<string, unknown>> = {}): string | null {
  if (code === "plan_full") return planFullText(details);
  if (code === "too_many_jobs") {
    const max = countOf(details, "max");
    return max === null
      ? "Сейчас готовятся страницы сразу из нескольких PDF. Когда один из них будет готов, попробуйте снова"
      : `Сейчас готовятся страницы сразу из ${String(max)} PDF. Когда один из них будет готов, попробуйте снова`;
  }
  if (code === "too_many_pages") {
    const max = countOf(details, "max");
    return max === null
      ? "Выбрано больше страниц, чем можно взять за один раз. Выберите меньше"
      : `За один раз можно взять не больше ${String(max)} ${ruPlural(max, "страницы", "страниц", "страниц")}. Выберите меньше`;
  }
  return IMPORT_REFUSAL_TEXT[code] ?? null;
}

/**
 * Активное содержимое словами.
 *
 * ⚠️ Это ПРЕДУПРЕЖДЕНИЕ, а не отказ, и решение осознанное: у нас такое
 * содержимое не исполняется никогда, документ открывается на чтение, формы и
 * JavaScript выключены, а на выходе получается картинка. Отказывать значило бы
 * отклонять учебники, у которых `/OpenAction` стоит ради начального масштаба, а
 * `/EmbeddedFile` ради приложенного аудио. Сказать при этом стоит: человек мог
 * не знать, что в присланном ему файле есть сценарии.
 *
 * Отказываем мы только на шифровании: там читать нечего в буквальном смысле.
 *
 * ⚠️ Наружу словарь отдаётся целиком, а не только через `importWarningText` ниже:
 * админская панель считает маркеры поштучно (разрез «что реально встречается в
 * файлах»), и ей нужно имя ОДНОГО маркера, а не фраза про весь файл. Второй
 * словарь тех же пяти строк разошёлся бы с этим при первом же новом маркере.
 */
export const IMPORT_WARNING_TEXT: Readonly<Record<string, string>> = {
  "/JavaScript": "сценарии JavaScript",
  "/OpenAction": "действие при открытии",
  "/Launch": "запуск внешней программы",
  "/EmbeddedFile": "вложенные файлы",
  "/RichMedia": "встроенное мультимедиа",
};

export function importWarningText(warnings: readonly string[]): string | null {
  const named = warnings.map((w) => IMPORT_WARNING_TEXT[w]).filter((t) => t !== undefined);
  if (named.length === 0) return null;
  return (
    `В этом PDF есть активное содержимое (${named.join(", ")}). ` +
    "Мы его не запускаем: страницы просто рисуются картинками. " +
    "Но если файл прислали со стороны, стоит знать"
  );
}
